/**
 * The share dialog.
 *
 * Follows the prototype closely — 560px, radius 22, invite row, people list,
 * three link-permission cards, the link row with Copy, the password switch —
 * and adds the things end-to-end encryption forces it to say.
 *
 * Three honesty requirements the prototype could not have known about:
 *
 *  1. **The link secret is in the fragment.** It is generated here and never
 *     sent, so the URL shown is the *only* copy. Losing it means the link is
 *     unrecoverable, and the dialog says so rather than pretending it can be
 *     shown again later.
 *
 *  2. **A viewer holds the key.** Read-only is enforced by the server on
 *     writes; it cannot be enforced against someone who already decrypted the
 *     file. The role descriptions say what is actually guaranteed.
 *
 *  3. **Revoking does not un-know a key.** It stops the server serving that
 *     person, immediately, which is real — but anything already downloaded
 *     stays readable. The footnote states it plainly.
 *
 * The prototype's password switch defaults to on. Kept, because the design and
 * the security posture agree for once.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { api, ApiError, errorText, type LinkWire, type Role, type ShareWire } from '../api';
import { ds } from '../ds';
import { useApp } from '../state';
import { entitlements } from '@shared/entitlements';
import { embedFile, embedOrigin } from '../embeds';
import type { EmbedWire } from '../api';
import { Avatar, Footnote } from './Shell';
import { createShareLink, suggestLinkPassword, wrapKeyForRecipient } from '../crypto/share';
import { readableFingerprint } from '../crypto/keys';
import type { DecryptedNode } from '../nodes';
import { formatBytes, monoPlain, SCRIM, SHADOW } from '../theme';
import { useT } from '../i18n';

const ROLES: { key: Role; label: string; description: string }[] = [
  { key: 'viewer', label: 'Can view', description: 'Read and download' },
  { key: 'commenter', label: 'Can comment', description: 'Read, download and leave notes' },
  { key: 'editor', label: 'Can edit', description: 'Change and replace the file' }
];

export function ShareDialog({
  node,
  nodeKey,
  onClose
}: {
  node: DecryptedNode;
  nodeKey: CryptoKey | null;
  onClose: () => void;
}) {
  const t = useT();
  const { Button, Icon, Input, Switch } = ds();

  const [people, setPeople] = useState<ShareWire[]>([]);
  const [links, setLinks] = useState<LinkWire[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('commenter');
  const [linkRole, setLinkRole] = useState<Role>('commenter');
  const [usePassword, setUsePassword] = useState(true);
  const { me } = useApp();
  // Server-side truth is that the counts are recorded for everyone and read back
  // by whoever asks; this is the client half of the double gate, so the feature
  // reads as paid rather than as missing.
  const canSeeAnalytics = entitlements(me?.plan).linkAnalytics;
  // The client half of the same double gate. The server refuses regardless;
  // this is what makes the feature read as paid rather than as missing.
  const canEmbed = entitlements(me?.plan).fileEmbeds;

  const [embeds, setEmbeds] = useState<EmbedWire[]>([]);
  const [embedding, setEmbedding] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);

  /** The one that is serving, if any. A node can have had several over time;
   *  only one of them answers. */
  const liveEmbed = embeds.find((e) => e.state === 'live') ?? null;

  const embedUrlFor = (embed: EmbedWire) => `${embedOrigin()}/e/${embed.slug}`;

  const startEmbedding = async () => {
    if (!nodeKey) return;
    setEmbedding(true);
    setError(null);
    try {
      await embedFile(node, nodeKey, node.name, node.mime || 'application/octet-stream');
      setEmbeds((await api.embeds.list()).embeds.filter((e) => e.nodeId === node.id));
      setEmbedCopied(false);
    } catch (err) {
      setError(errorText(err, 'That file could not be embedded.'));
    } finally {
      setEmbedding(false);
    }
  };

  const stopEmbedding = async (id: string) => {
    setEmbedding(true);
    try {
      await api.embeds.remove(id);
      setEmbeds((await api.embeds.list()).embeds.filter((e) => e.nodeId === node.id));
    } catch (err) {
      setError(errorText(err, 'That embed could not be revoked.'));
    } finally {
      setEmbedding(false);
    }
  };

  const [password, setPassword] = useState(() => suggestLinkPassword());
  // Expiry and a download cap have been in the API since the first migration and
  // were never exposed. Both are offered to **every** plan: they are security
  // controls, and docs/BACKLOG.md already settled that those do not go behind a
  // paywall — the same reasoning that made password-protected links free.
  const [expiryDays, setExpiryDays] = useState<number | null>(null);
  const [downloadCap, setDownloadCap] = useState<number | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, l] = await Promise.all([api.shares.forNode(node.id), api.links.forNode(node.id)]);
      setPeople(p.shares);
      setLinks(l.links);
    } catch {
      // A listing failure should not stop someone sharing.
    }
  }, [node.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [onClose]);

  /**
   * Invite by email.
   *
   * Two outcomes. If they have a vault, their public key is fetched and the
   * node key is wrapped to it here — the server never sees it unwrapped. If
   * they do not, the invite is recorded as pending and the key is wrapped later,
   * once they have a key to wrap to.
   */
  const invite = async () => {
    if (!email.trim()) return;
    setBusy(true);
    setError(null);

    try {
      let wrapped: string | undefined;
      let fingerprint: string | undefined;

      try {
        const recipient = await api.vault.publicKeyFor(email.trim());
        if (!nodeKey) throw new Error('This file’s key is not loaded. Reopen the folder.');
        wrapped = await wrapKeyForRecipient(recipient.publicKey, nodeKey);
        fingerprint = recipient.fingerprint;
      } catch (err) {
        // 404 means "no vault yet", which is a pending invite rather than a
        // failure. Anything else is real.
        if (!(err instanceof ApiError && err.status === 404)) throw err;
      }

      await api.shares.invite(node.id, {
        email: email.trim(),
        role,
        nodeKeyWrapped: wrapped,
        recipientFingerprint: fingerprint
      });

      setEmail('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send that invitation.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Create a link.
   *
   * The secret is generated here, the node key is wrapped under a key derived
   * from it, and only the wrap plus a bearer token reach the server. The secret
   * itself lives in the fragment of the URL below and nowhere else.
   */
  const makeLink = async () => {
    if (!nodeKey) {
      setError('This file’s key is not loaded. Reopen the folder and try again.');
      return;
    }
    setBusy(true);
    setError(null);

    try {
      const created = await createShareLink(nodeKey, {
        password: usePassword ? password : undefined
      });

      const { id } = await api.links.create(node.id, {
        token: created.token,
        nodeKeyWrapped: created.nodeKeyWrapped,
        role: linkRole,
        passwordSalt: created.passwordSalt,
        passwordVerifier: created.passwordVerifier,
        // Seconds, and absolute — the server validates against its own clock, so
        // sending a duration would make the link's life depend on how far the
        // visitor's browser is out.
        ...(expiryDays ? { expiresAt: Math.floor(Date.now() / 1000) + expiryDays * 86400 } : {}),
        ...(downloadCap ? { maxDownloads: downloadCap } : {})
      });

      // The path segment is only there to make the URL readable — the opener
      // re-derives the bearer token from the fragment, so the id is not a
      // credential and revealing it grants nothing.
      setShareUrl(`${window.location.origin}/s/${id}#${created.fragment}`);
      await load();
    } catch (err) {
      setError(errorText(err, 'Could not create that link.'));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!shareUrl) return;
    await navigator.clipboard?.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      onClick={onClose}
      className="mv-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        background: SCRIM,
        display: 'grid',
        placeItems: 'center',
        zIndex: 20,
        // The bottom inset keeps the dialog clear of the home indicator and of
        // the mobile tab bar it renders over.
        padding: '20px 16px calc(20px + env(safe-area-inset-bottom, 0px))'
      }}
    >
      <div
        role="dialog"
        aria-label={`Share ${node.name}`}
        onClick={(e) => e.stopPropagation()}
        className="mv-pop"
        style={{
          width: '100%',
          maxWidth: 560,
          // 90vh leaves the dialog under the browser's own chrome on a phone,
          // where the visible viewport is shorter than the layout one. `dvh`
          // tracks the chrome; the vh value is the fallback for older engines.
          maxHeight: 'min(90vh, 92dvh)',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          background: 'var(--surface-card)',
          borderRadius: 22,
          boxShadow: SHADOW.xl
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '24px 28px 18px',
            borderBottom: '1px solid var(--border-subtle)'
          }}
        >
          <span
            style={{
              width: 40,
              height: 40,
              borderRadius: 13,
              background: 'var(--primary-soft)',
              color: 'var(--primary-text)',
              display: 'grid',
              placeItems: 'center'
            }}
          >
            <Icon name={node.kind === 'folder' ? 'folder' : 'file-text'} size={19} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                fontFamily: "var(--font-display), 'Baloo 2', system-ui",
                fontWeight: 800,
                fontSize: 21,
                margin: 0,
                color: 'var(--text-strong)'
              }}
            >
              Share “{node.name}”.
            </h2>
            <div style={monoPlain}>
              {node.kind === 'folder'
                ? 'Folder · everything inside is shared too'
                : formatBytes(node.plainSizeBytes)}
            </div>
          </div>
          <button aria-label={t('share-dialog.close')} onClick={onClose} style={closeButton}>
            <Icon name="x" size={16} />
          </button>
        </header>

        <div style={{ padding: '22px 28px', display: 'grid', gap: 22 }}>
          <section style={{ display: 'grid', gap: 10 }}>
            <SectionLabel>{t('share-dialog.invite-people')}</SectionLabel>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Input
                  type="email"
                  placeholder={t('share-dialog.email-address')}
                  value={email}
                  onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
                />
              </div>
              <Button variant="primary" size="md" disabled={busy} onClick={() => void invite()}>
                {t('share-dialog.invite')}
              </Button>
            </div>
            <RolePicker value={role} onChange={setRole} compact />
          </section>

          {people.length > 0 && (
            <section style={{ display: 'grid', gap: 10 }}>
              {people.map((person) => (
                <div key={person.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Avatar name={person.email} email={person.email} size={34} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>
                      {person.email}
                    </div>
                    <div style={monoPlain}>
                      {person.state === 'pending'
                        ? 'Invited · no vault yet'
                        : person.fingerprint
                          ? `key ${readableFingerprint(person.fingerprint).slice(0, 14)}`
                          : 'accepted'}
                    </div>
                    {person.keyChanged && (
                      <div style={{ ...monoPlain, color: 'var(--danger-600)' }}>
                        Their encryption key changed since you shared this. Check with them
                        before trusting it.
                      </div>
                    )}
                  </div>
                  <span style={{ ...monoPlain, textTransform: 'none' }}>
                    {ROLES.find((r) => r.key === person.role)?.label ?? person.role}
                  </span>
                  <button
                    aria-label={t('share-dialog.remove')}
                    onClick={async () => {
                      await api.shares.revoke(person.id);
                      await load();
                    }}
                    style={closeButton}
                  >
                    <Icon name="x" size={14} />
                  </button>
                </div>
              ))}
            </section>
          )}

          <section style={{ display: 'grid', gap: 12 }}>
            <SectionLabel>{t('share-dialog.anyone-with-the-link')}</SectionLabel>
            <RolePicker value={linkRole} onChange={setLinkRole} />

            <div
              style={{
                border: '1px solid var(--border-subtle)',
                borderRadius: 12,
                padding: '14px 16px',
                display: 'grid',
                gap: 12
              }}
            >
              <Switch
                label={t('share-dialog.ask-for-a-password')}
                description="People need it before the link opens."
                checked={usePassword}
                onChange={(next: boolean) => setUsePassword(next)}
              />
              {usePassword && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      type="text"
                      value={password}
                      onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
                    />
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPassword(suggestLinkPassword())}
                  >
                    {t('share-dialog.generate')}
                  </Button>
                </div>
              )}

              <ChipRow
                label={t('share-dialog.stops-working-after')}
                value={expiryDays}
                onChange={setExpiryDays}
                options={[
                  { label: 'Never', value: null },
                  { label: '24 hours', value: 1 },
                  { label: '7 days', value: 7 },
                  { label: '30 days', value: 30 },
                  { label: '90 days', value: 90 }
                ]}
              />

              <ChipRow
                label={t('share-dialog.download-limit')}
                value={downloadCap}
                onChange={setDownloadCap}
                options={[
                  { label: 'No limit', value: null },
                  { label: '1', value: 1 },
                  { label: '5', value: 5 },
                  { label: '25', value: 25 }
                ]}
              />
            </div>

            {shareUrl ? (
              <div style={{ display: 'grid', gap: 8 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    height: 44,
                    padding: '0 14px',
                    borderRadius: 12,
                    background: 'var(--surface-sunken)'
                  }}
                >
                  <Icon name="link-2" size={15} />
                  <span
                    style={{
                      ...monoPlain,
                      fontSize: 12,
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {shareUrl}
                  </span>
                  <Button variant="soft" size="sm" onClick={() => void copy()}>
                    {copied ? 'Copied' : 'Copy link'}
                  </Button>
                </div>
                <Warning>{t('share-dialog.copy-this-now-it-cannot')}</Warning>
              </div>
            ) : (
              <Button variant="secondary" size="md" disabled={busy} onClick={() => void makeLink()}>
                {t('share-dialog.create-a-link')}
              </Button>
            )}

            {links.length > 0 && (
              <div style={{ display: 'grid', gap: 8 }}>
                {links.map((l) => (
                  <LinkRow key={l.id} link={l} showCounts={canSeeAnalytics} />
                ))}
                <div style={monoPlain}>
                  {links.length} active link{links.length === 1 ? '' : 's'} ·{' '}
                  {links.filter((l) => l.hasPassword).length} with a password ·{' '}
                  <span
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={async () => {
                      for (const link of links) await api.links.revoke(link.id);
                      setShareUrl(null);
                      await load();
                    }}
                  >
                    {t('share-dialog.revoke-all')}
                  </span>
                </div>
                {!canSeeAnalytics && (
                  <Footnote>
                    {t('share-dialog.max-shows-how-many-times')}
                  </Footnote>
                )}
              </div>
            )}
          </section>

          {/*
            Embedding, and only for a file.

            Last in the dialog and visually separated, because it is the one
            control here that turns the encryption off for the thing it acts on.
            Everything above it — a person, a link, a password — keeps the file
            in the vault; this takes a copy out, and the sentence under the
            button says so before anybody presses it rather than after.
          */}
          {canEmbed && node.kind === 'file' && (
            <section style={{ display: 'grid', gap: 12 }}>
              <SectionLabel>{t('share-dialog.embed-anywhere')}</SectionLabel>

              {liveEmbed ? (
                <div style={{ display: 'grid', gap: 8 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      height: 44,
                      padding: '0 14px',
                      borderRadius: 12,
                      background: 'var(--surface-sunken)'
                    }}
                  >
                    <Icon name="image" size={15} />
                    <span
                      style={{
                        ...monoPlain,
                        fontSize: 12,
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {embedUrlFor(liveEmbed)}
                    </span>
                    <Button
                      variant="soft"
                      size="sm"
                      onClick={() => {
                        void navigator.clipboard.writeText(embedUrlFor(liveEmbed));
                        setEmbedCopied(true);
                      }}
                    >
                      {embedCopied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={embedding}
                    onClick={() => void stopEmbedding(liveEmbed.id)}
                  >
                    {t('share-dialog.stop-embedding')}
                  </Button>
                  {/* The one property of this feature that cannot be undone. */}
                  <Warning>{t('share-dialog.turning-this-off-deletes')}</Warning>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  <Button
                    variant="secondary"
                    size="md"
                    disabled={embedding || !nodeKey}
                    onClick={() => void startEmbedding()}
                  >
                    {embedding
                      ? t('share-dialog.copying-out-of-your')
                      : t('share-dialog.create-an-embed-link')}
                  </Button>
                  <Footnote>{t('share-dialog.this-takes-a-copy')}</Footnote>
                </div>
              )}
            </section>
          )}

          {error && (
            <div style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</div>
          )}
        </div>

        <footer
          style={{
            padding: '16px 28px 20px',
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12
          }}
        >
          <span style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {t('share-dialog.revoking-stops-the-link-opening')}
          </span>
          <Button variant="primary" size="md" onClick={onClose}>
            {t('share-dialog.done')}
          </Button>
        </footer>
      </div>
    </div>
  );
}

// ── pieces ──────────────────────────────────────────────────────────────────

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontFamily: "var(--font-mono), monospace",
      fontSize: 10.5,
      letterSpacing: '.17em',
      textTransform: 'uppercase',
      color: 'var(--text-faint)'
    }}
  >
    {children}
  </div>
);

const Warning = ({ children }: { children: React.ReactNode }) => (
  <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-muted)', margin: 0 }}>
    {children}
  </p>
);

const closeButton: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 10,
  border: 'none',
  background: 'transparent',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  flex: '0 0 32px'
};

function RolePicker({
  value,
  onChange,
  compact = false
}: {
  value: Role;
  onChange: (role: Role) => void;
  compact?: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        // Both variants auto-fit now. The compact one was pinned at three
        // columns, which put "Can comment" into about 90px on a phone.
        gridTemplateColumns: `repeat(auto-fit, minmax(${compact ? 104 : 150}px, 1fr))`,
        gap: 8
      }}
    >
      {ROLES.map((option) => {
        const selected = option.key === value;
        return (
          <button
            key={option.key}
            onClick={() => onChange(option.key)}
            style={{
              textAlign: 'left',
              padding: compact ? '8px 10px' : '11px 13px',
              borderRadius: 12,
              cursor: 'pointer',
              background: selected ? 'var(--primary-soft)' : 'var(--surface-card)',
              border: `1px solid ${selected ? 'var(--primary)' : 'var(--border-subtle)'}`,
              color: selected ? 'var(--primary-text)' : 'var(--text-body)'
            }}
          >
            <div style={{ fontSize: 13.5, fontWeight: selected ? 600 : 500 }}>{option.label}</div>
            {!compact && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                {option.description}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A labelled row of mutually exclusive chips.
 *
 * Chips rather than a `<select>` because both settings have four or five values
 * and no sensible free-form input: a duration typed as a number needs a unit
 * beside it, and the unit is where "1" becomes ambiguous. Every option is
 * visible, so the default — "Never", "No limit" — is visibly a *choice* rather
 * than an empty field somebody forgot.
 */
function ChipRow({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: number | null;
  onChange: (next: number | null) => void;
  options: { label: string; value: number | null }[];
}) {
  return (
    <div style={{ display: 'grid', gap: 7 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)' }}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.label}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(opt.value)}
              style={{
                padding: '5px 11px',
                borderRadius: 999,
                cursor: 'pointer',
                fontSize: 12.5,
                fontWeight: active ? 700 : 500,
                border: `1px solid ${active ? 'var(--accent-600)' : 'var(--border-subtle)'}`,
                background: active ? 'var(--accent-100)' : 'transparent',
                color: active ? 'var(--accent-600)' : 'var(--text-body)'
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One existing link: what limits it carries, and — on Max — how it is doing.
 *
 * The counts are recorded for every link on every plan; `showCounts` gates
 * reading them back, not collecting them. Collecting conditionally would mean a
 * downgrade silently erased history somebody might later pay to see.
 */
function LinkRow({ link, showCounts }: { link: LinkWire; showCounts: boolean }) {
  const bits: string[] = [link.role];
  if (link.hasPassword) bits.push('password');
  if (link.expiresAt) {
    const days = Math.ceil((link.expiresAt * 1000 - Date.now()) / 86_400_000);
    bits.push(days <= 0 ? 'expired' : `${days}d left`);
  }
  if (link.maxDownloads) bits.push(`${link.downloadCount}/${link.maxDownloads} downloads`);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        borderRadius: 10,
        background: 'var(--surface-sunken)',
        fontSize: 12.5
      }}
    >
      <span style={{ color: 'var(--text-muted)' }}>{bits.join(' · ')}</span>
      {showCounts && (
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
          {link.viewCount} opened · {link.downloadCount} downloaded
        </span>
      )}
    </div>
  );
}
